import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {ProgressComponent} from '../progress/progress.component';
import {Location} from '../../models/location';
import {map, switchMap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {from, Observable, Subscription, timer} from 'rxjs';
import {Location as AngularLocation} from '@angular/common';
import '@firebase/firestore';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BreadCrumb} from '../../models/breadcrumb';
import {DialogService} from '../../services/dialog.service';
import {CanComponentDeactivate} from '../../guards/can-deactive.guard';
import {
    addDoc,
    collection,
    doc,
    docData,
    DocumentData,
    DocumentReference,
    Firestore, getDocs,
    query,
    serverTimestamp, updateDoc, where
} from '@angular/fire/firestore';
import {BreadcrumbsComponent} from '../breadcrumbs/breadcrumbs.component';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatError, MatInput} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {MatFormField} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';

@Component({
    selector: 'app-location-form',
    templateUrl: './location-form.component.html',
    styleUrls: ['./location-form.component.css'],
    imports: [
        ProgressComponent,
        BreadcrumbsComponent,
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardContent,
        ReactiveFormsModule,
        MatFormField,
        MatError,
        MatInput,
        MatSelect,
        MatOption,
        MatButton
    ],
    standalone: true
})
export class LocationFormComponent implements OnInit, OnDestroy, CanComponentDeactivate {

    @ViewChild(ProgressComponent, {static: true}) progress: ProgressComponent;

    facilityId: string;
    locationId: string;
    accountId: string;
    location: Observable<DocumentData>;
    breadcrumbs: BreadCrumb[];
    facilityName: string;
    facility: Observable<DocumentData>;
    locationForm: FormGroup;
    types = [];
    private authStateSubscription: Subscription;
    private locationSubscription: Subscription;
    private facilityDocument: DocumentReference;
    private locationDocument: DocumentReference;

    constructor(
        private formBuilder: FormBuilder,
        private firestore: Firestore,
        private router: Router,
        private activeRoute: ActivatedRoute,
        private angularLocation: AngularLocation,
        private snackBar: MatSnackBar,
        private dialogService: DialogService) {
    }

    ngOnInit() {
        this.breadcrumbs = [
            {url: ['/'], title: 'Accounts'},
            {url: null, title: 'Facilities'},
            {url: ['../'], title: 'Facility'},
            {url: null, title: 'New Location'},
        ];
        this.activeRoute.paramMap.subscribe(
            params => {
                this.locationId = params.get('locationId');
                this.facilityId = params.get('facilityId');
                if (this.locationId) {
                    this.fetchLocation();
                }
                this.locationForm = this.formBuilder.group({
                    name: ['', Validators.required, this.validateUniqueName.bind(this)],
                    type: ['', Validators.required],
                });
                const facilityDocRef = doc(this.firestore, 'facilities', this.facilityId);
                this.facilityDocument = facilityDocRef;
                this.facility = docData(facilityDocRef);

                this.facility.subscribe(data => {
                    this.facilityName = data.name;
                    if (data.type === 1) {
                        this.types = [
                            {name: 'Shipping', value: 1},
                        ];
                    } else {
                        this.types = [
                            {name: 'On Hold', value: 2},
                            {name: 'Receiving', value: 3},
                            {name: 'Jobsite', value: 4},
                            {name: 'Inventory', value: 5}
                        ];
                    }
                    this.breadcrumbs[2].title = `Facility: ${data.name}`;
                    this.breadcrumbs[1].url = ['../../../../' + data.accountId];
                });
            }
        );
    }

    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.locationForm.dirty) {
            const confirm: Observable<boolean> = this.dialogService.confirm('Discard changes for Location?');
            confirm.subscribe(conf => {
                if (!conf) {
                    this.angularLocation.go(this.router.url);
                    return false;
                }
            });
            return confirm;
        }
        return true;
    }

    async submit() {
        this.progress.setVisible(true);
        const basePath = doc(this.firestore, 'facilities', this.facilityId);
        const locationsColRef = collection(basePath, 'locations');
        if (this.locationId) {
            const locationDocRef = doc(basePath, 'locations', this.locationId);
            await updateDoc(locationDocRef, {
                ...this.locationForm.value,
                updated: serverTimestamp()
            });
        } else {
            const newLocation: Partial<Location> = {
                created: serverTimestamp(),
                updated: serverTimestamp(),
                ...this.locationForm.value
            };
            await addDoc(locationsColRef, newLocation);
        }
        this.progress.setVisible(false);
        this.snackBar.open(
            `Location ${this.locationForm.value.name} ${this.locationId ? 'Updated' : 'Created'}`,
            null,
            { duration: 3000 }
        );
        this.locationForm.markAsPristine();
        this.angularLocation.back();
    }

    validateUniqueName(control: AbstractControl): Observable<ValidationErrors | null> {
        return timer(500).pipe(
            switchMap(() => {
                const locationsColRef = collection(
                    doc(this.firestore, 'facilities', this.facilityId),
                    'locations'
                );
                const nameQuery = query(
                    locationsColRef,
                    where('name', '==', control.value)
                );
                return from(getDocs(nameQuery)).pipe(
                    map(snapshot => {
                        if (snapshot.empty) {
                            return null;
                        }
                        if (this.locationId) {
                            const duplicate = snapshot.docs.some(docSnap => docSnap.id !== this.locationId);
                            return duplicate ? { duplicate: true } : null;
                        }
                        return { duplicate: true };
                    })
                );
            })
        );
    }


    fetchLocation() {
        this.progress.setVisible(true);
        const locationDocRef = doc(
            this.firestore,
            'facilities',
            this.facilityId,
            'locations',
            this.locationId
        );
        this.locationDocument = locationDocRef;
        this.location = docData(locationDocRef);

        this.locationSubscription = this.location.subscribe(data => {
            this.breadcrumbs[2].url = ['../../'];
            this.breadcrumbs[3].url = null;
            this.breadcrumbs[3].title = 'Edit Location: ' + data.name;
            this.locationForm.patchValue(data);
            this.locationForm.get('name').markAsTouched();
            this.progress.setVisible(false);
        });
    }

    ngOnDestroy(): void {
        if (this.locationSubscription) {
            this.locationSubscription.unsubscribe();
        }
        if (this.authStateSubscription) {
            this.authStateSubscription.unsubscribe();
        }
    }
}
